// LE CHAMP DE CHROMEFLOW, sans GPU : les métaballes, leur maillage par
// marching TETRAHEDRA (six tétraèdres par cellule : seize cas, une table
// triviale, correcte par construction ; les facettes ne se voient pas parce
// que les normales viennent du GRADIENT du champ, pas des triangles), les
// quatre formes (goutte, anneau, colonne, nuée) et la source du Web Worker
// qui fait tout cela hors du fil principal.
//
// Unités : le cube [-1, 1]³ autour du centre de composition. Une boule =
// (x, y, z, R) ; noyau de Wyvill (1 - d²/R²)³ à support fini (R), iso 0,25 :
// le rayon visible d'une boule isolée vaut 0,63 R. Les boules se fondent
// l'une dans l'autre dès que leurs supports se recouvrent : les gouttes
// éjectées rejoignent la masse sans jamais sauter.

export const ISO = 0.25;
export const VISIBLE = Math.sqrt(1 - Math.cbrt(ISO));      // rayon visible / R
export const MAX_BALLS = 64;

// --- les fonctions du worker (autonomes : pas de capture du module) ---------

// le champ sur la grille (N + 1)³, par boîte englobante de chaque boule
function buildField(F, N, balls, nb) {
  F.fill(0);
  var S = N + 1, h = 2 / N;
  for (var b = 0; b < nb; b++) {
    var cx = balls[4 * b], cy = balls[4 * b + 1], cz = balls[4 * b + 2], R = balls[4 * b + 3];
    if (R <= 0.001) continue;
    var R2 = R * R, inv = 1 / R2;
    var i0 = Math.max(0, Math.floor((cx - R + 1) / h)), i1 = Math.min(N, Math.ceil((cx + R + 1) / h));
    var j0 = Math.max(0, Math.floor((cy - R + 1) / h)), j1 = Math.min(N, Math.ceil((cy + R + 1) / h));
    var k0 = Math.max(0, Math.floor((cz - R + 1) / h)), k1 = Math.min(N, Math.ceil((cz + R + 1) / h));
    for (var k = k0; k <= k1; k++) {
      var dz = -1 + k * h - cz, dz2 = dz * dz;
      if (dz2 > R2) continue;
      for (var j = j0; j <= j1; j++) {
        var dy = -1 + j * h - cy, dyz = dz2 + dy * dy;
        if (dyz > R2) continue;
        var row = (k * S + j) * S;
        for (var i = i0; i <= i1; i++) {
          var dx = -1 + i * h - cx, d2 = dyz + dx * dx;
          if (d2 < R2) { var t = 1 - d2 * inv; F[row + i] += t * t * t; }
        }
      }
    }
  }
}

// le maillage : positions et normales (gradient du champ, interpolé le long de
// l'arête) ; renvoie le nombre de sommets écrits (3 par triangle)
function marchTets(F, N, iso, pos, nrm, maxVerts) {
  var S = N + 1, h = 2 / N, out = 0;
  // les 6 tétraèdres de Kuhn autour de la DIAGONALE DU CUBE 0-7 (coins : bit0 x, bit1 y, bit2 z) :
  // les six chemins d'arêtes de 0 à 7 (une première version partageait l'arête 0-6, une
  // diagonale de FACE : les tétraèdres ne pavaient pas le cube, la surface avait des trous en anneaux)
  var TETS = [0, 1, 3, 7, 0, 1, 5, 7, 0, 2, 3, 7, 0, 2, 6, 7, 0, 4, 5, 7, 0, 4, 6, 7];
  var cornerIdx = new Int32Array(8), cornerVal = new Float64Array(8);
  var P = new Float64Array(12), G = new Float64Array(12);      // les 4 sommets du tétraèdre : positions, gradients
  var E = new Float64Array(18), EN = new Float64Array(18);      // jusqu'à 6 points d'arête
  function gradAt(idx, i, j, k, g, o) {
    // différences centrales (décentrées au bord) ; la normale sortante est -grad
    var gx = (i < N ? F[idx + 1] : F[idx]) - (i > 0 ? F[idx - 1] : F[idx]);
    var gy = (j < N ? F[idx + S] : F[idx]) - (j > 0 ? F[idx - S] : F[idx]);
    var gz = (k < N ? F[idx + S * S] : F[idx]) - (k > 0 ? F[idx - S * S] : F[idx]);
    g[o] = -gx; g[o + 1] = -gy; g[o + 2] = -gz;
  }
  function emit(e0, e1, e2) {
    if (out + 3 > maxVerts) return;
    var es = [e0, e1, e2];
    for (var q = 0; q < 3; q++) {
      var e = es[q], o = out * 3;
      pos[o] = E[e * 3]; pos[o + 1] = E[e * 3 + 1]; pos[o + 2] = E[e * 3 + 2];
      var nx = EN[e * 3], ny = EN[e * 3 + 1], nz = EN[e * 3 + 2], l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
      nrm[o] = nx / l; nrm[o + 1] = ny / l; nrm[o + 2] = nz / l;
      out++;
    }
  }
  var tval = new Float64Array(4), ins = new Int32Array(4), outs = new Int32Array(4);
  function edge(a, b, e) {
    var va = tval[a] - iso, vb = tval[b] - iso;
    var t = va / (va - vb);
    for (var c = 0; c < 3; c++) { E[e * 3 + c] = P[a * 3 + c] + (P[b * 3 + c] - P[a * 3 + c]) * t; EN[e * 3 + c] = G[a * 3 + c] + (G[b * 3 + c] - G[a * 3 + c]) * t; }
  }
  for (var k = 0; k < N; k++) for (var j = 0; j < N; j++) for (var i = 0; i < N; i++) {
    // un cube entièrement dehors (le cas courant) ou dedans : rien
    var any = false, all = true;
    for (var c = 0; c < 8; c++) {
      var idx = ((k + ((c >> 2) & 1)) * S + j + ((c >> 1) & 1)) * S + i + (c & 1);
      cornerIdx[c] = idx; cornerVal[c] = F[idx];
      if (F[idx] > iso) any = true; else all = false;
    }
    if (!any || all) continue;
    for (var t = 0; t < 6; t++) {
      var inside = 0, ni = 0, no = 0;
      for (var q = 0; q < 4; q++) {
        var c2 = TETS[t * 4 + q];
        var ii = i + (c2 & 1), jj = j + ((c2 >> 1) & 1), kk = k + ((c2 >> 2) & 1);
        P[q * 3] = -1 + ii * h; P[q * 3 + 1] = -1 + jj * h; P[q * 3 + 2] = -1 + kk * h;
        gradAt(cornerIdx[c2], ii, jj, kk, G, q * 3);
        tval[q] = cornerVal[c2];
        if (tval[q] > iso) { inside++; ins[ni++] = q; } else outs[no++] = q;
      }
      if (inside === 0 || inside === 4) continue;
      if (inside === 1) { edge(ins[0], outs[0], 0); edge(ins[0], outs[1], 1); edge(ins[0], outs[2], 2); emit(0, 1, 2); }
      else if (inside === 3) { edge(outs[0], ins[0], 0); edge(outs[0], ins[1], 1); edge(outs[0], ins[2], 2); emit(0, 1, 2); }
      else {
        // deux dedans, deux dehors : un quadrilatère
        edge(ins[0], outs[0], 0); edge(ins[0], outs[1], 1); edge(ins[1], outs[1], 2); edge(ins[1], outs[0], 3);
        emit(0, 1, 2); emit(0, 2, 3);
      }
    }
  }
  return out;
}

export { buildField, marchTets };

// la source du worker : les deux fonctions, et le protocole
//   -> { id, N, balls: Float32Array(4 n), nb, pos, nrm }   (pos / nrm : tampons à réutiliser)
//   <- { id, pos, nrm, count, ms }
export function fieldWorkerSource() {
  return 'var ISO = ' + ISO + ';\n' +
    'var buildField = ' + buildField.toString() + ';\n' +
    'var marchTets = ' + marchTets.toString() + ';\n' +
    'var F = null, FN = -1;\n' +
    'self.onmessage = function (ev) {\n' +
    '  var m = ev.data, t0 = performance.now();\n' +
    '  if (FN !== m.N) { FN = m.N; F = new Float32Array((m.N + 1) * (m.N + 1) * (m.N + 1)); }\n' +
    '  buildField(F, m.N, m.balls, m.nb);\n' +
    '  var count = marchTets(F, m.N, ISO, m.pos, m.nrm, m.pos.length / 3);\n' +
    '  self.postMessage({ id: m.id, pos: m.pos, nrm: m.nrm, count: count, ms: performance.now() - t0 }, [m.pos.buffer, m.nrm.buffer]);\n' +
    '};\n';
}

// --- les formes (fil principal) -----------------------------------------------
// -> tableau de boules [x, y, z, R] pour une forme, au temps t (s) ; au plus 16
export const SHAPES = ['drop', 'ring', 'column', 'swarm'];
export function shapeBalls(shape, t) {
  const out = [];
  if (shape === 'drop') {
    out.push([0, 0.02 * Math.sin(t * 0.9), 0, 0.62]);
    for (let i = 0; i < 5; i++) {
      const a = t * (0.35 + 0.07 * i) + i * 1.257, e = 0.5 + 0.1 * Math.sin(t * 0.5 + i);
      out.push([Math.cos(a) * 0.52, Math.sin(t * 0.7 + i * 2.1) * 0.3, Math.sin(a) * 0.52 * e, 0.30]);
    }
  } else if (shape === 'ring') {
    const tilt = 0.5 + 0.3 * Math.sin(t * 0.3), ca = Math.cos(tilt), sa = Math.sin(tilt);
    for (let i = 0; i < 8; i++) {
      const a = t * 0.4 + i * Math.PI / 4;
      const x = Math.cos(a) * 0.58, z = Math.sin(a) * 0.58;
      out.push([x, z * sa, z * ca, 0.36]);
    }
  } else if (shape === 'column') {
    for (let i = 0; i < 6; i++) {
      const y = -0.65 + i * 0.26, sw = 0.12 * Math.sin(t * 1.1 + i * 0.8);
      out.push([sw, y, 0.12 * Math.cos(t * 0.9 + i * 0.8), 0.36 - 0.02 * Math.abs(i - 2.5)]);
    }
  } else {
    for (let i = 0; i < 12; i++) {
      const a = t * (0.5 + 0.1 * (i % 4)) + i * 0.524, r = 0.5 + 0.17 * Math.sin(i * 1.7 + t * 0.3);
      const ax = i % 3;
      const u = Math.cos(a) * r, v = Math.sin(a) * r;
      out.push(ax === 0 ? [u, v, 0.2 * Math.sin(t + i), 0.26] : ax === 1 ? [u, 0.2 * Math.cos(t * 0.8 + i), v, 0.26] : [0.2 * Math.sin(t * 0.6 + i), u, v, 0.26]);
    }
  }
  return out;
}
// la boule unique de la montée (sphère parfaite qui vibre)
export function sphereBall(t) { return [[0, 0, 0, 0.78 + 0.012 * Math.sin(t * 2 * Math.PI * 12)]]; }

// les gouttes du drop : 40 sur des orbites (angle de départ, rayon, axe)
export function dropOrbit(i, t) {
  const r = 0.7 + 0.35 * ((i * 7) % 11) / 11, w = 1.6 + 0.6 * ((i * 3) % 5) / 5, a = i * 2.399 + t * w;
  const ax = i % 3, u = Math.cos(a) * r, v = Math.sin(a) * r, s = 0.25 * Math.sin(i + t * 0.7);
  return ax === 0 ? [u, v, s] : ax === 1 ? [u, s, v] : [s, u, v];
}
